// Package 4, part L (owner, 7. 10. 2026): the temporary sounds, made in code until the real files come (the list and
// their licenses: docs/AUDIO_LICENSES.md). Each sound is rendered once into a small mono WAV (22 050 Hz, 16 bit) and
// handed to Howler as a blob URL. Effects are short and rendered when the game starts; ambience and music loops are
// rendered the first time a screen asks for them.

export const SAMPLE_RATE = 22050;

export type EffectId =
  | 'report_open'
  | 'loot_common'
  | 'loot_uncommon'
  | 'loot_rare'
  | 'loot_epic'
  | 'loot_legendary'
  | 'level_up'
  | 'craft_done'
  | 'goal_claimed'
  | 'key_moment'
  | 'radio_jingle'
  // 11. 10. 2026 (owner, docs/thepit.md): the Pit's replay: a hit, a critical hit, a miss.
  | 'pit_hit'
  | 'pit_crit'
  | 'pit_miss';
export type LoopId = 'amb_camp' | 'amb_map' | 'amb_horde' | 'mus_camp' | 'mus_horde' | 'mus_finale' | 'radio_static';

type Wave = 'sine' | 'triangle' | 'square' | 'saw';

/** A small seeded random (the same sound every time). */
function rand(seed: number) {
  let x = seed >>> 0 || 1;
  return () => {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    return ((x >>> 0) % 100000) / 100000;
  };
}

const osc = (wave: Wave, phase: number) => {
  const p = phase - Math.floor(phase);
  if (wave === 'sine') return Math.sin(2 * Math.PI * p);
  if (wave === 'triangle') return 4 * Math.abs(p - 0.5) - 1;
  if (wave === 'square') return p < 0.5 ? 1 : -1;
  return 2 * p - 1;
};

/** Adds a note into `out` at `start` seconds: an attack and an exponential decay. */
function note(out: Float32Array, start: number, freq: number, dur: number, opts: { wave?: Wave; vol?: number; attack?: number; decay?: number; vibrato?: number } = {}) {
  const { wave = 'sine', vol = 0.3, attack = 0.005, decay = dur / 3, vibrato = 0 } = opts;
  const s0 = Math.floor(start * SAMPLE_RATE);
  const n = Math.floor(dur * SAMPLE_RATE);
  let phase = 0;
  for (let i = 0; i < n && s0 + i < out.length; i++) {
    const t = i / SAMPLE_RATE;
    const env = (t < attack ? t / attack : Math.exp(-(t - attack) / decay)) * (i > n - 200 ? (n - i) / 200 : 1);
    phase += (freq * (1 + vibrato * Math.sin(2 * Math.PI * 5 * t))) / SAMPLE_RATE;
    out[s0 + i] += osc(wave, phase) * env * vol;
  }
}

/** Adds filtered noise (one-pole low-pass: `tone` 0–1, higher is brighter) with an envelope function. */
function noise(out: Float32Array, start: number, dur: number, vol: number, tone: number, env: (t: number) => number, seed = 1) {
  const r = rand(seed);
  const s0 = Math.floor(start * SAMPLE_RATE);
  const n = Math.floor(dur * SAMPLE_RATE);
  let y = 0;
  for (let i = 0; i < n && s0 + i < out.length; i++) {
    y += tone * (r() * 2 - 1 - y);
    out[s0 + i] += y * vol * env(i / SAMPLE_RATE);
  }
}

const hz = (semi: number) => 440 * Math.pow(2, semi / 12);

/** Notes for a find by rarity: more and higher for a better one. */
const LOOT: Record<string, number[]> = {
  loot_common: [3],
  loot_uncommon: [3, 7],
  loot_rare: [3, 7, 10],
  loot_epic: [3, 7, 10, 15],
  loot_legendary: [3, 7, 10, 15, 19, 22],
};

/** An effect's samples. */
export function renderEffect(id: EffectId): Float32Array {
  switch (id) {
    case 'report_open': {
      const out = new Float32Array(Math.floor(0.45 * SAMPLE_RATE));
      noise(out, 0, 0.35, 0.35, 0.35, (t) => Math.sin(Math.PI * Math.min(1, t / 0.35)) ** 2);
      note(out, 0.05, hz(-12), 0.3, { wave: 'triangle', vol: 0.15 });
      return out;
    }
    case 'level_up': {
      const out = new Float32Array(Math.floor(1.3 * SAMPLE_RATE));
      [0, 4, 7, 12].forEach((s, i) => note(out, i * 0.12, hz(s), 0.7, { wave: 'triangle', vol: 0.22, decay: 0.3 }));
      note(out, 0.48, hz(12), 0.8, { wave: 'sine', vol: 0.18, decay: 0.4, vibrato: 0.004 });
      return out;
    }
    case 'craft_done': {
      const out = new Float32Array(Math.floor(0.9 * SAMPLE_RATE));
      note(out, 0, 1320, 0.8, { wave: 'sine', vol: 0.18, decay: 0.15 });
      note(out, 0, 1985, 0.6, { wave: 'sine', vol: 0.1, decay: 0.1 });
      noise(out, 0, 0.04, 0.4, 0.9, (t) => 1 - t / 0.04, 7);
      note(out, 0.18, 1320, 0.6, { wave: 'sine', vol: 0.1, decay: 0.12 });
      return out;
    }
    case 'goal_claimed': {
      const out = new Float32Array(Math.floor(0.9 * SAMPLE_RATE));
      note(out, 0, hz(7), 0.5, { wave: 'triangle', vol: 0.22, decay: 0.2 });
      note(out, 0.14, hz(12), 0.7, { wave: 'triangle', vol: 0.22, decay: 0.3 });
      return out;
    }
    case 'key_moment': {
      const out = new Float32Array(Math.floor(1.4 * SAMPLE_RATE));
      note(out, 0, 55, 1.2, { wave: 'sine', vol: 0.45, decay: 0.35 });
      noise(out, 0, 0.12, 0.25, 0.2, (t) => 1 - t / 0.12, 3);
      note(out, 0.2, hz(-9), 1.1, { wave: 'saw', vol: 0.05, attack: 0.5, decay: 0.4 });
      return out;
    }
    case 'pit_hit': {
      // A dull thump.
      const out = new Float32Array(Math.floor(0.25 * SAMPLE_RATE));
      note(out, 0, 90, 0.2, { wave: 'sine', vol: 0.4, decay: 0.05 });
      noise(out, 0, 0.06, 0.25, 0.3, (t) => 1 - t / 0.06, 31);
      return out;
    }
    case 'pit_crit': {
      // A harder thump with a ring.
      const out = new Float32Array(Math.floor(0.5 * SAMPLE_RATE));
      note(out, 0, 70, 0.3, { wave: 'sine', vol: 0.5, decay: 0.08 });
      noise(out, 0, 0.08, 0.35, 0.5, (t) => 1 - t / 0.08, 37);
      note(out, 0.02, hz(19), 0.4, { wave: 'triangle', vol: 0.08, decay: 0.12 });
      return out;
    }
    case 'pit_miss': {
      // A swish of air.
      const out = new Float32Array(Math.floor(0.22 * SAMPLE_RATE));
      noise(out, 0, 0.2, 0.18, 0.75, (t) => Math.sin(Math.PI * Math.min(1, t / 0.2)), 41);
      return out;
    }
    case 'radio_jingle': {
      const out = new Float32Array(Math.floor(1.4 * SAMPLE_RATE));
      [0, 4, 7].forEach((s, i) => note(out, i * 0.16, hz(s + 3), 0.45, { wave: 'square', vol: 0.08, decay: 0.15 }));
      note(out, 0.5, hz(15), 0.8, { wave: 'triangle', vol: 0.18, decay: 0.35, vibrato: 0.006 });
      return out;
    }
    default: {
      const out = new Float32Array(Math.floor(1.2 * SAMPLE_RATE));
      LOOT[id].forEach((s, i) => note(out, i * 0.09, hz(s + 12), 0.6, { wave: 'sine', vol: 0.2, decay: 0.25 }));
      if (id === 'loot_legendary') note(out, 0.55, hz(34), 0.6, { wave: 'triangle', vol: 0.1, decay: 0.3, vibrato: 0.01 });
      return out;
    }
  }
}

/** A loop's samples (it starts and ends quiet so it loops without a click). */
export function renderLoop(id: LoopId): Float32Array {
  const seconds = id.startsWith('mus') ? 16 : id === 'radio_static' ? 3 : 8;
  const out = new Float32Array(seconds * SAMPLE_RATE);
  const r = rand(id.length * 97);
  if (id === 'radio_static') noise(out, 0, seconds, 0.12, 0.9, () => 1, 11);
  if (id === 'amb_camp') {
    // A fire: a low rumble that breathes slowly. 23. 10. 2026 (owner, a player's feedback): without the crackles (40 sharp
    // bursts in an 8-second loop sounded like clicking).
    noise(out, 0, seconds, 0.18, 0.04, (t) => 0.8 + 0.2 * Math.sin((2 * Math.PI * t) / seconds), 5);
  }
  if (id === 'amb_map') noise(out, 0, seconds, 0.3, 0.02, (t) => 0.6 + 0.4 * Math.sin((2 * Math.PI * t) / seconds), 9);
  if (id === 'amb_horde') {
    noise(out, 0, seconds, 0.35, 0.015, () => 1, 13);
    for (let i = 0; i < 5; i++) note(out, r() * (seconds - 1.5), 70 + r() * 40, 1.4, { wave: 'saw', vol: 0.05, attack: 0.3, decay: 0.5, vibrato: 0.03 });
  }
  if (id === 'mus_camp') {
    // A slow minor progression on soft pads: Am, F, C, G.
    const chords = [
      [0, 3, 7],
      [-4, 0, 3],
      [3, 7, 10],
      [-2, 2, 5],
    ];
    chords.forEach((c, i) => c.forEach((s) => note(out, i * 4, hz(s - 12), 4.2, { wave: 'triangle', vol: 0.06, attack: 1.2, decay: 2.5 })));
    [12, 10, 7, 5, 3, 7].forEach((s, i) => note(out, 1 + i * 2.5, hz(s - 12), 1.8, { wave: 'sine', vol: 0.05, attack: 0.1, decay: 0.8 }));
  }
  if (id === 'mus_horde') {
    for (let b = 0; b < seconds * 2; b++) {
      note(out, b * 0.5, 60, 0.35, { wave: 'sine', vol: 0.35, decay: 0.08 });
      if (b % 2) noise(out, b * 0.5, 0.08, 0.15, 0.6, (t) => 1 - t / 0.08, 20 + b);
    }
    [0, 0, 3, -2].forEach((s, i) => note(out, i * 4, hz(s - 24), 3.8, { wave: 'saw', vol: 0.05, attack: 0.4, decay: 2 }));
  }
  if (id === 'mus_finale') {
    [0, 3, 7, 12, 10, 7, 3, 2, 0, 3, 7, 5, 3, 2, -2, 0].forEach((s, i) => note(out, i, hz(s), 1.6, { wave: 'sine', vol: 0.12, attack: 0.05, decay: 0.7 }));
    [[-12, -9, -5], [-14, -10, -7], [-16, -12, -9], [-12, -9, -5]].forEach((c, i) => c.forEach((s) => note(out, i * 4, hz(s), 4.2, { wave: 'triangle', vol: 0.04, attack: 1.5, decay: 2.5 })));
  }
  // Soft ends for a clean loop.
  const fade = Math.floor(0.05 * SAMPLE_RATE);
  for (let i = 0; i < fade; i++) {
    out[i] *= i / fade;
    out[out.length - 1 - i] *= i / fade;
  }
  return out;
}

/** A mono 16-bit WAV of the samples. */
export function wav(samples: Float32Array): ArrayBuffer {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const str = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples.length * 2, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, SAMPLE_RATE, true);
  v.setUint32(28, SAMPLE_RATE * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, 'data');
  v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) v.setInt16(44 + i * 2, Math.max(-1, Math.min(1, samples[i])) * 0x7fff, true);
  return buf;
}

/** A blob URL Howler can play. */
export function wavUrl(samples: Float32Array): string {
  return URL.createObjectURL(new Blob([wav(samples)], { type: 'audio/wav' }));
}
